import { useState, useEffect } from "react";
import {
  Typography, Button, Table, Space, Modal, Form, Input,
  Upload, Tag, Popconfirm, message, Card, Empty, Tooltip,
} from "antd";
import {
  UploadOutlined, DeleteOutlined, EditOutlined,
  FilePdfOutlined, LinkOutlined, PlusOutlined,
} from "@ant-design/icons";
import {
  getNotaryDocuments,
  uploadNotaryDocument,
  updateNotaryDocument,
  deleteNotaryDocument,
} from "../../api/portalService";

const { Title, Text } = Typography;
const { TextArea } = Input;

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NotaryDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  const fetchDocuments = () => {
    setLoading(true);
    getNotaryDocuments()
      .then(({ data }) => setDocuments(data.results || []))
      .catch(() => message.error("No se pudieron cargar los documentos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const openCreate = () => {
    setEditDoc(null);
    form.resetFields();
    setFileList([]);
    setModalOpen(true);
  };

  const openEdit = (doc) => {
    setEditDoc(doc);
    form.setFieldsValue({ title: doc.title, description: doc.description, order: doc.order });
    setFileList([]);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotaryDocument(id);
      message.success("Documento eliminado");
      fetchDocuments();
    } catch {
      message.error("No se pudo eliminar el documento");
    }
  };

  const handleSubmit = async (values) => {
    setUploading(true);
    try {
      if (editDoc) {
        await updateNotaryDocument(editDoc.id, {
          title: values.title,
          description: values.description || "",
          order: values.order ? parseInt(values.order) : 0,
        });
        message.success("Documento actualizado");
      } else {
        if (fileList.length === 0) {
          message.error("Selecciona un archivo PDF");
          setUploading(false);
          return;
        }
        const fd = new FormData();
        fd.append("file", fileList[0].originFileObj);
        fd.append("title", values.title);
        fd.append("description", values.description || "");
        fd.append("order", values.order ? parseInt(values.order) : 0);
        await uploadNotaryDocument(fd);
        message.success("Documento subido correctamente");
      }
      setModalOpen(false);
      fetchDocuments();
    } catch (e) {
      message.error(e?.response?.data?.message || "Error al guardar el documento");
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: "Orden",
      dataIndex: "order",
      key: "order",
      width: 70,
      align: "center",
    },
    {
      title: "Documento",
      key: "title",
      render: (_, doc) => (
        <Space direction="vertical" size={2}>
          <Space>
            <FilePdfOutlined style={{ color: "#ff4d4f" }} />
            <Text strong>{doc.title}</Text>
          </Space>
          {doc.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>{doc.description}</Text>
          )}
          <Text type="secondary" style={{ fontSize: 11 }}>{doc.fileName}</Text>
        </Space>
      ),
    },
    {
      title: "Tamaño",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 90,
      render: (v) => <Tag>{formatBytes(v)}</Tag>,
    },
    {
      title: "Subido",
      dataIndex: "uploadedAt",
      key: "uploadedAt",
      width: 110,
      render: (v) => v ? new Date(v).toLocaleDateString("es-CL") : "—",
    },
    {
      title: "Link",
      key: "link",
      width: 70,
      align: "center",
      render: (_, doc) => (
        <Tooltip title="Ver / Descargar">
          <Button
            icon={<LinkOutlined />}
            size="small"
            type="link"
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
          />
        </Tooltip>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 110,
      render: (_, doc) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(doc)}
          />
          <Popconfirm
            title="¿Eliminar este documento?"
            onConfirm={() => handleDelete(doc.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: "#1d3557" }}>
            Documentos del portal
          </Title>
          <Text type="secondary">
            Gestiona los PDFs disponibles para descarga en el portal de clientes.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          style={{ background: "#1d3557", borderColor: "#1d3557" }}
        >
          Subir documento
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }}>
        <Table
          columns={columns}
          dataSource={documents}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty description="Sin documentos aún" /> }}
          pagination={false}
        />
      </Card>

      <Modal
        title={editDoc ? "Editar documento" : "Subir nuevo documento"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item
            label="Título"
            name="title"
            rules={[{ required: true, message: "El título es requerido" }]}
          >
            <Input placeholder="Ej: Nómina del personal" />
          </Form.Item>

          <Form.Item label="Descripción" name="description">
            <TextArea
              rows={2}
              placeholder="Descripción opcional del documento"
            />
          </Form.Item>

          <Form.Item label="Orden de aparición" name="order">
            <Input type="number" min={0} placeholder="0" style={{ width: 120 }} />
          </Form.Item>

          {!editDoc && (
            <Form.Item label="Archivo PDF" required>
              <Upload
                accept=".pdf"
                maxCount={1}
                fileList={fileList}
                beforeUpload={() => false}
                onChange={({ fileList: fl }) => setFileList(fl)}
              >
                <Button icon={<UploadOutlined />}>Seleccionar PDF</Button>
              </Upload>
            </Form.Item>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={uploading}
              style={{ background: "#1d3557", borderColor: "#1d3557" }}
            >
              {editDoc ? "Guardar cambios" : "Subir"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default NotaryDocuments;
